import type { ComponentProps } from '../blocks';
import { CHART_COLORS, CHART_FONT, type ChartColors } from './colors';
import { xmlEscape } from './escape';

/** Viewport + padding for the area chart. Shared by the React primitive and the rasterizer. */
export interface AreaDims {
  W: number;
  H: number;
  padL: number;
  padR: number;
  padT: number;
  padB: number;
}

/** Which axis labels an Area chart will draw — drives how much padding the plot needs. */
export interface AreaLabels {
  /** Category labels under the x-axis (from `xLabels`). */
  xLabels: boolean;
  /** x-axis title (from `xLabel`). */
  xTitle: boolean;
  /** y-axis title (from `yLabel`). */
  yTitle: boolean;
}

/** Read which labels a set of Area props asks for. */
export function areaLabelsOf(props: ComponentProps['Area']): AreaLabels {
  return {
    xLabels: !!(props.xLabels && props.xLabels.length),
    xTitle: !!props.xLabel,
    yTitle: !!props.yLabel,
  };
}

/**
 * Padding for the given label set. Y-axis value ticks are always drawn, so the
 * left gutter is always wide enough for them; x-labels/axis-titles widen the
 * relevant edge only when present.
 */
export function areaDims(labels: AreaLabels): AreaDims {
  return {
    W: 720,
    H: 240,
    padL: 46 + (labels.yTitle ? 16 : 0),
    padR: 14,
    padT: 14,
    padB: 24 + (labels.xLabels ? 16 : 0) + (labels.xTitle ? 16 : 0),
  };
}

/** Default dims: y-axis ticks only (no x-labels or axis titles). */
export const AREA_DIMS: AreaDims = areaDims({ xLabels: false, xTitle: false, yTitle: false });

/** Round `range` to a "nice" value (1/2/5 × 10ⁿ). */
function niceNum(range: number, round: boolean): number {
  const exp = Math.floor(Math.log10(range));
  const f = range / Math.pow(10, exp);
  let nf: number;
  if (round) {
    nf = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
  } else {
    nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  }
  return nf * Math.pow(10, exp);
}

/** A nice min/max/step spanning [min, max] with ~`maxTicks` round gridlines. */
function niceScale(min: number, max: number, maxTicks: number): { niceMin: number; niceMax: number; step: number } {
  // Flat (or inverted, from a nonsensical floor > hi) — give it a unit range to
  // tick over; a non-positive range would otherwise make niceNum's log10 NaN.
  if (max - min <= 0) max = min + 1;
  const range = niceNum(max - min, false);
  const step = niceNum(range / Math.max(1, maxTicks - 1), true);
  return {
    niceMin: Math.floor(min / step) * step,
    niceMax: Math.ceil(max / step) * step,
    step,
  };
}

/** Compact y-axis tick label: 1.2M, 900k, 0.5. */
export function formatTick(v: number): string {
  const a = Math.abs(v);
  const trim = (n: number) => String(Math.round(n * 10) / 10);
  if (a >= 1e9) return `${trim(v / 1e9)}B`;
  if (a >= 1e6) return `${trim(v / 1e6)}M`;
  if (a >= 1e3) return `${trim(v / 1e3)}k`;
  return trim(v);
}

/**
 * Pick up to `max` evenly-spaced indices in `[0, count)` for sparse x-axis
 * labels (so a 30-day series shows ~6 dates, not all 30).
 */
export function sparseXTicks(count: number, max = 6): number[] {
  if (count <= 0) return [];
  if (count === 1) return [0];
  const n = Math.min(count, max);
  const idx = Array.from({ length: n }, (_, i) => Math.round((i / (n - 1)) * (count - 1)));
  return Array.from(new Set(idx));
}

/** Text anchor for an x-axis label at index `i`: edges anchor inward, middles center. */
export function xTickAnchor(i: number, count: number): 'start' | 'middle' | 'end' {
  return i === 0 ? 'start' : i === count - 1 ? 'end' : 'middle';
}

/** Pure coordinate/path math for an area chart — no React, no DOM, no color. */
export interface AreaGeometry {
  linePath: string;
  areaPath: string;
  /** Y-axis ticks: the round value at each gridline and its pixel y. */
  yTicks: { value: number; y: number }[];
  /** Per-datum pixel coordinates (for hover dots in the React primitive). */
  points: { x: number; y: number }[];
  lo: number;
  hi: number;
  min: number;
  max: number;
  scaleX: (i: number) => number;
  scaleY: (v: number) => number;
  dims: AreaDims;
}

/**
 * Compute the area-chart geometry. Caller must guard `data.length === 0`
 * (an empty series makes `min`/`max` non-finite — there is nothing to draw).
 */
export function areaGeometry(data: number[], floor?: number, dims: AreaDims = AREA_DIMS): AreaGeometry {
  const { W, H, padL, padR, padT, padB } = dims;
  const lo = Math.min(...data);
  const hi = Math.max(...data);
  // Round axis so y ticks read as clean numbers; baseline at `floor` when given
  // (clamped to the data max so a floor above every point can't invert the scale).
  const base = floor != null ? Math.min(floor, hi) : lo;
  const { niceMin: min, niceMax: max, step } = niceScale(base, hi, 4);
  const span = max - min || 1;
  const denom = data.length - 1 || 1;
  const scaleX = (i: number) => padL + (i / denom) * (W - padL - padR);
  const scaleY = (v: number) => padT + (1 - (v - min) / span) * (H - padT - padB);
  const linePath = data
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${scaleX(i).toFixed(1)},${scaleY(v).toFixed(1)}`)
    .join(' ');
  const areaPath = `${linePath} L${scaleX(data.length - 1).toFixed(1)},${H - padB} L${scaleX(0).toFixed(1)},${H - padB} Z`;
  const yTicks: { value: number; y: number }[] = [];
  for (let v = min; v <= max + step / 2; v += step) {
    yTicks.push({ value: v, y: scaleY(v) });
  }
  const points = data.map((v, i) => ({ x: scaleX(i), y: scaleY(v) }));
  return { linePath, areaPath, yTicks, points, lo, hi, min, max, scaleX, scaleY, dims };
}

/** Append a short unit (e.g. "%", "GB") to a y-tick; long units belong in the axis title. */
export function tickUnit(unit: string): string {
  return unit && unit.length <= 3 ? unit : '';
}

/**
 * Standalone, self-contained area-chart SVG for rasterization (Slack PNGs).
 * No interactivity/animation — that lives only in the React primitive.
 */
export function areaSvg(props: ComponentProps['Area'], colors: ChartColors = CHART_COLORS): string {
  const { title, data, unit = '', floor, xLabels, xLabel, yLabel } = props;
  const labels = areaLabelsOf(props);
  const dims = areaDims(labels);
  const { W, H, padL, padR } = dims;
  const titleH = title ? 34 : 0;
  const totalH = H + titleH + 8;

  if (data.length === 0) {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${totalH}" width="${W}" height="${totalH}" font-family='${CHART_FONT}'>` +
      `<rect width="${W}" height="${totalH}" fill="${colors.bg}"/>` +
      (title ? `<text x="0" y="22" font-size="15" font-weight="600" fill="${colors.text}">${xmlEscape(title)}</text>` : '') +
      `<text x="${W / 2}" y="${totalH / 2}" text-anchor="middle" font-size="13" fill="${colors.subtext}">No data to chart.</text></svg>`;
  }

  const g = areaGeometry(data, floor, dims);
  const u = tickUnit(unit);
  const grid = g.yTicks
    .map(
      (t) =>
        `<line x1="${padL}" y1="${t.y.toFixed(1)}" x2="${W - padR}" y2="${t.y.toFixed(1)}" stroke="${colors.grid}" stroke-width="1"/>` +
        `<text x="${padL - 8}" y="${(t.y + 4).toFixed(1)}" text-anchor="end" font-size="11" fill="${colors.subtext}">${xmlEscape(formatTick(t.value) + u)}</text>`,
    )
    .join('');

  const xTicks =
    xLabels && xLabels.length
      ? sparseXTicks(xLabels.length)
          .filter((i) => i < data.length)
          .map((i) => {
            const x = g.scaleX(i);
            return `<text x="${x.toFixed(1)}" y="${H - (labels.xTitle ? 22 : 6)}" text-anchor="${xTickAnchor(i, xLabels.length)}" font-size="11" fill="${colors.subtext}">${xmlEscape(xLabels[i])}</text>`;
          })
          .join('')
      : '';

  const xTitleEl = xLabel
    ? `<text x="${(padL + (W - padR)) / 2}" y="${H - 4}" text-anchor="middle" font-size="11" font-weight="600" fill="${colors.subtext}">${xmlEscape(xLabel)}</text>`
    : '';
  const yTitleEl = yLabel
    ? `<text x="14" y="${H / 2}" text-anchor="middle" font-size="11" font-weight="600" fill="${colors.subtext}" transform="rotate(-90 14 ${H / 2})">${xmlEscape(yLabel)}</text>`
    : '';

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${totalH}" width="${W}" height="${totalH}" font-family='${CHART_FONT}'>` +
    `<rect width="${W}" height="${totalH}" fill="${colors.bg}"/>` +
    (title ? `<text x="0" y="22" font-size="15" font-weight="600" fill="${colors.text}">${xmlEscape(title)}</text>` : '') +
    `<g transform="translate(0 ${titleH})">` +
    `<defs><linearGradient id="heArea" x1="0" y1="0" x2="0" y2="1">` +
    `<stop offset="0%" stop-color="${colors.purple}" stop-opacity="0.22"/>` +
    `<stop offset="100%" stop-color="${colors.purple}" stop-opacity="0"/></linearGradient></defs>` +
    grid +
    `<path d="${g.areaPath}" fill="url(#heArea)"/>` +
    `<path d="${g.linePath}" fill="none" stroke="${colors.purple}" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>` +
    xTicks +
    xTitleEl +
    yTitleEl +
    `</g></svg>`
  );
}
